import { gql, useQuery } from "@apollo/client";
import { useMemo } from "react";
import PatientDetailScreen from "~/components/patient/PatientDetailScreen";
import { PatientDetailScreenQuery } from "~/graphql/__generated__/graphql";
import { useSnackbar } from "~/hooks/useSnackbar";
import { useTodaysVisits } from "~/providers/TodaysVisitsProvider";

type Props = {
  patientId: number;
}

export default function PatientDetailView({ patientId }: Props) {
  const { setErrorMessage } = useSnackbar();

  // In order not to need to frequently poll the patient data,
  // we use an up-to-date view of only today's visits (already needed by other components)
  const { visits } = useTodaysVisits();
  const todaysVisits = useMemo(() => visits.filter(v => v.patient?.id === patientId), [visits, patientId]);

  const { data, refetch } = useQuery<PatientDetailScreenQuery>(PATIENT_QUERY, {
    variables: {
      patientId,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError(error) {
      console.error(error);
      setErrorMessage('There was an error fetching patient information');
    },
  });

  const loading = !data;

  return (
    <PatientDetailScreen
      patient={data?.patientSingle}
      visits={data?.visit || []}
      todaysVisits={todaysVisits}
      refetch={refetch}
      loading={loading}
    />
  );
}

export const PATIENT_QUERY = gql`
  query PatientDetailScreen($patientId: Int)
  @permission(requires: useWebApp)
  {
    patientSingle(where: { id: { eq: $patientId } }) {
      id
      firstName
      lastName
      phone
      preferredLanguage
      sex
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressCoordinates
      addressTimezone
      dateOfBirth
      patientNotes
      serviceInstructions

      patientIdentifiers {
        identifier
        identifier_type
      }

      episodes {
        id
        active
        serviceLineId
        organizationalUnitId
        updatedAt
        precautions {
          id
          precautionId
        }
      }

      careTeamProviders(where: { deleted: { eq: false } }) {
        id
        allowAutomaticUpdates
        providerId
        providerTypeId
        provider {
          id
          firstName
          lastName
          providerTypeId
        }
      }
    }

    visit(where: {
      patientId: { eq: $patientId }
    }) {
      id
      startTime
      endTime
      eventStatus
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressCoordinates
      addressTimezone
      serviceInstructions
      arrivalWindowStartTime
      arrivalWindowEndTime
      episode {
        serviceLineId
      }
      visitResources {
        providerId
        provider {
          id
          firstName
          lastName
        }
      }
      visitType {
        id
        name
      }
      visitServices {
        id
        serviceId
        service {
          id
          name
        }
      }
      cancellationReason {
        id
        description
      }
    }
  }
`;
